
import React, { useState, useEffect } from 'react';
import { Category, Account } from '@/types';
import { CategoryItemHeader } from './CategoryItemHeader';
import { CategoryItemContent } from './CategoryItemContent';
import { CategoryDeleteDialog } from './CategoryDeleteDialog';
import { CategorySelectionWrapper } from './CategorySelectionWrapper';
import { checkForLinkedAccounts } from '@/utils/categoryUtils';
import { toast } from 'sonner';

interface CategoryItemProps {
  category: Category;
  accounts: Account[];
  onDrop: (accountId: string, categoryId: string) => void;
  onToggle: (categoryId: string) => void;
  expanded: Set<string>;
  level: number;
  index: number;
  onEdit: (category: Category) => void;
  totalItems: number;
  parentId?: string;
  onRemoveAccount?: (accountId: string) => void;
  onMoveUp?: (categoryId: string, parentId: string | null) => void;
  onMoveDown?: (categoryId: string, parentId: string | null) => void;
  isFirst?: boolean;
  isLast?: boolean;
  onDeleteCategory?: (categoryId: string) => void;
  onSelect?: (category: Category) => void;
  isSelected?: boolean;
  selectedCategory?: Category | null;
}

export const CategoryItem: React.FC<CategoryItemProps> = ({ 
  category, 
  accounts, 
  onDrop, 
  onToggle, 
  expanded, 
  level, 
  index, 
  onEdit, 
  totalItems, 
  parentId, 
  onRemoveAccount,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onDeleteCategory,
  onSelect,
  isSelected = false,
  selectedCategory
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isExpanded = expanded.has(category.id);
  const hasChildren = category.children.length > 0 || category.accounts.length > 0;
  const hasLinkedAccounts = checkForLinkedAccounts(category);
  
  // Force update when selectedCategory changes
  const [, forceUpdate] = useState({});
  
  // Check if this category is selected based on ID comparison
  const isCategorySelected = selectedCategory?.id === category.id;
  
  useEffect(() => {
    // Update component when selection state changes
    forceUpdate({});
  }, [selectedCategory]);
  
  const handleDelete = () => {
    if (hasChildren) {
      setShowDeleteDialog(true);
    } else {
      onDeleteCategory?.(category.id);
      toast.success('Category deleted successfully');
    }
  };

  return (
    <CategorySelectionWrapper
      category={category}
      isSelected={isCategorySelected}
      onSelect={onSelect!}
    >
      <div 
        id={`category-${category.id}`}
        className={`relative ${level > 0 ? 'ml-8 pl-4 border-l border-[#333333] border-opacity-20' : ''} 
          ${isExpanded ? 'expanded' : ''}`}
      >
        <CategoryItemHeader
          name={category.name}
          isExpanded={isExpanded}
          hasChildren={hasChildren}
          onClick={() => onToggle(category.id)}
          onEdit={() => onEdit(category)}
          onDelete={onDeleteCategory ? handleDelete : undefined}
          isOver={false}
          canDrop={false}
          level={level}
          type={category.type}
          onMoveUp={() => onMoveUp?.(category.id, parentId)}
          onMoveDown={() => onMoveDown?.(category.id, parentId)}
          isFirst={isFirst}
          isLast={isLast}
          hasLinkedAccounts={hasLinkedAccounts}
          isSelected={isCategorySelected}
        />
        
        {isExpanded && (
          <CategoryItemContent
            category={category}
            accounts={accounts}
            onDrop={onDrop}
            onToggle={onToggle}
            expanded={expanded}
            level={level}
            onEdit={onEdit}
            onRemoveAccount={onRemoveAccount}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDeleteCategory={onDeleteCategory}
            onSelect={onSelect}
            isSelected={isSelected}
            selectedCategory={selectedCategory}
          />
        )}

        <CategoryDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirmDelete={() => {
            onDeleteCategory?.(category.id);
            setShowDeleteDialog(false);
          }}
        />
      </div>
    </CategorySelectionWrapper>
  );
};
